import type { AudioProbe } from './audio-probe';

export type PlaybackMetadata = AudioProbe & { byteVerified: true; format: string; mime?: string };
type ProbeResolution = { transport?: string; nativeCandidate?: boolean; byteVerified?: boolean };
type ProbeSnapshot = { resolution: object | null; trackId: string | null; mediaUrl: string | null };
type ProbeOptions = {
  resolution: ProbeResolution; trackId: string; exactTrackId: string; mediaUrl: string; signal: AbortSignal;
  current: () => ProbeSnapshot;
  fetcher: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  apply: (metadata: PlaybackMetadata) => void;
  timeoutMs?: number;
};

// A metadata response may only fill these measured fields. It cannot change
// playback identity, transport, requested quality, or native error recovery.
export function playbackMetadataPatch(value: unknown): PlaybackMetadata | null {
  if (!value || typeof value !== 'object') return null;
  const data = value as Record<string, unknown>;
  if (data.byteVerified !== true || typeof data.format !== 'string' || !/^[a-z0-9][a-z0-9_-]{0,15}$/i.test(data.format) || data.format.toLowerCase() === 'unknown') return null;
  const number = (key: string, maximum: number, integer = false) => typeof data[key] === 'number' && Number.isFinite(data[key]) && data[key] > 0 && data[key] <= maximum && (!integer || Number.isInteger(data[key])) ? data[key] as number : null;
  const bitrate = number('bitrate', 100000000);
  const aac = data.codec === 'aac' && ['mp4', 'm4a', 'aac'].includes(data.format.toLowerCase());
  return {
    byteVerified: true, format: data.format.toUpperCase(),
    ...(aac ? {codec: 'aac' as const} : {}),
    ...(typeof data.mime === 'string' && /^audio\/[a-z0-9.+-]{1,64}$/i.test(data.mime) ? { mime: data.mime.toLowerCase() } : {}),
    bitrate, sampleRate: number('sampleRate', 768000, true), bitsPerSample: aac ? null : number('bitsPerSample', 64, true),
    channels: number('channels', 64, true), encodedDuration: number('encodedDuration', 604800),
    bitrateBasis: bitrate !== null && data.bitrateBasis === 'mpeg-frame' ? 'mpeg-frame' : null,
  };
}

/** Read the already selected URL in the background; never resolve or play it. */
export async function probePlaybackMetadata(options: ProbeOptions): Promise<boolean> {
  const { resolution, trackId, exactTrackId, mediaUrl, signal } = options;
  if (signal.aborted || resolution.transport !== 'browser' || resolution.nativeCandidate !== true || resolution.byteVerified === true) return false;
  const current = () => {
    const value = options.current();
    return !signal.aborted && value.resolution === resolution && value.trackId === trackId && value.mediaUrl === mediaUrl;
  };
  if (!current()) return false;
  // Avoid relying on AbortSignal.any/timeout, absent in older WebViews.
  const controller = new AbortController();
  const cancel = () => controller.abort();
  signal.addEventListener('abort', cancel, { once: true });
  const timeout = setTimeout(cancel, options.timeoutMs ?? 8000);
  let finishCancellation: (() => void) | undefined;
  const cancelled = new Promise<null>(resolve => {
    finishCancellation = () => resolve(null);
    controller.signal.addEventListener('abort', finishCancellation, { once: true });
  });
  try {
    const request = (async () => {
      const response = await options.fetcher('/api/playback/probe', {
        method: 'POST', headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ trackId: exactTrackId, mediaUrl }), signal: controller.signal,
      });
      if (!response.ok || controller.signal.aborted) return null;
      const payload = await response.json();
      return playbackMetadataPatch(payload?.data);
    })().catch(() => null);
    const metadata = await Promise.race([request, cancelled]);
    if (!metadata || controller.signal.aborted || !current()) return false;
    options.apply(metadata);
    return true;
  } catch { return false; }
  finally {
    clearTimeout(timeout);
    signal.removeEventListener('abort', cancel);
    if (finishCancellation) controller.signal.removeEventListener('abort', finishCancellation);
  }
}
